// mantine
import {
    Button,
    Drawer,
    Loader,
    Select,
    Table,
    TextInput
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
// components
import { Plus } from 'lucide-react';
import { TAccountLink, TRole, TAccountLinkData } from '@newo/api/gateway';
import NoRecords from '@/components/NoRecords';
import MemberRow from '@/pages/Profile/components/Members/components/MembersTable/components/MemberRow';
import MemberForm from '@/pages/Profile/components/Members/components/MembersTable/components/MemberForm';
// icons
// types
import { ROLES } from '@/pages/Profile/components/Members/Members.data';
import { useProfileQuery } from '@/services/customer/profile/profile.queries.ts';

interface MembersTableProps {
    members: TAccountLink[];
    onDelete: (member: TAccountLink) => void;
    onSubmit: (data: TAccountLinkData, accountId?: string) => Promise<void>;
    isLoading: boolean;
    isSuccess: boolean;
}

const MembersTable = ({
    members,
    onDelete,
    onSubmit,
    isLoading,
    isSuccess
}: MembersTableProps) => {
    const [selectedMember, setSelectedMember] = useState<
        TAccountLink | undefined
    >();
    const { data: profile } = useProfileQuery();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRole, setSelectedRole] = useState<TRole | ''>('');
    const [isDrawerOpened, handlers] = useDisclosure(false);

    const filteredMembers = members.filter((member) => {
        const matchesEmail = member.email
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        const matchesRole = !selectedRole || member.role === selectedRole;
        return matchesEmail && matchesRole;
    });

    const handleRoleChange = (value: string | null) => {
        setSelectedRole(value as TRole | '');
    };

    const handleOpen = () => {
        handlers.open();
    };

    const handleClose = () => {
        handlers.close();
        setSelectedMember(undefined);
    };

    const handleFormSubmit = async (
        data: TAccountLinkData,
        accountId?: string
    ) => {
        await onSubmit(data, accountId);
        handleClose();
    };

    const handleEdit = (member: TAccountLink) => {
        setSelectedMember(member);
        handleOpen();
    };

    return (
        <div>
            <div className="flex flex-wrap items-end gap-4 mb-4">
                <TextInput
                    className="w-full md:max-w-xs"
                    label="Search by email"
                    placeholder="Start typing..."
                    value={searchQuery}
                    onChange={(event) => {
                        setSearchQuery(event.currentTarget.value);
                    }}
                />
                <Select
                    clearable
                    className="w-full md:max-w-52"
                    data={[{ value: '', label: 'All Roles' }, ...ROLES]}
                    label="Filter by role"
                    placeholder="Select role"
                    value={selectedRole}
                    onChange={handleRoleChange}
                />
                <Button
                    className="md:ml-auto"
                    leftSection={<Plus />}
                    size="xs"
                    onClick={handleOpen}
                >
                    Invite Member
                </Button>
            </div>
            <Table.ScrollContainer minWidth={500} type="native">
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Email</Table.Th>
                            <Table.Th className="text-right">Role</Table.Th>
                            <Table.Th className="w-10" />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {isSuccess && filteredMembers.length === 0 ? (
                            <Table.Tr>
                                <Table.Td colSpan={3}>
                                    <NoRecords />
                                </Table.Td>
                            </Table.Tr>
                        ) : undefined}

                        {isLoading ? (
                            <Table.Tr>
                                <Table.Td
                                    className="text-center py-20"
                                    colSpan={3}
                                >
                                    <Loader />
                                </Table.Td>
                            </Table.Tr>
                        ) : undefined}

                        {isSuccess && filteredMembers.length > 0
                            ? filteredMembers.map((member) => (
                                  <MemberRow
                                      key={member.account_id}
                                      member={member}
                                      onDelete={onDelete}
                                      onEdit={handleEdit}
                                  />
                              ))
                            : undefined}
                    </Table.Tbody>
                </Table>
            </Table.ScrollContainer>

            <Drawer
                opened={isDrawerOpened}
                position="right"
                size="md"
                title="Invite Member"
                onClose={handleClose}
            >
                <MemberForm
                    isNextiva={profile?.tenant === 'nextiva'}
                    member={selectedMember}
                    onClose={handleClose}
                    onSubmit={handleFormSubmit}
                />
            </Drawer>
        </div>
    );
};

export default MembersTable;
